import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  ListItem,
  OrderedList,
  SimpleGrid,
  Stack,
  Text,
  UnorderedList,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Role } from 'api/types';
import { motion } from 'framer-motion';
import useCreateUserMutation from 'hooks/mutations/useCreateUserMutation';
import useUpdateUserMutation from 'hooks/mutations/useUpdateUserMutation';
import useAllRolesQuery from 'hooks/useAllRolesQuery';
import i18n from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useUserContext } from '../../context/UserContextProvider';
import { createDropdownPlainOptions, createDropdownTranslatedStaticOptions } from '../../helpers/general';
import { dropdown, simpleInput } from '../../helpers/makeFormFields';
import useOPUserQuery from '../../hooks/useOPUserQuery';
import useSettingsQuery from '../../hooks/useSettingsQuery';
import UserRolesAndPermissions from './UserRolesAndPermissions';

function useUserSchema() {
  const { t } = useTranslation('user');
  return useMemo(
    () =>
      yup
        .object({
          name: yup
            .string()
            .label(t('name'))
            .required(t('form.name_required'))
            .matches(/^[a-zA-Z ]+$/, t('form.name_matches')),
          email: yup.string().label(t('email')).email(t('form.correct_email')).required(t('form.email_required')),
          language: yup.string().required(t('personal_settings.error_message.language')),
          timezone: yup.string().required(),
          spokenLanguages: yup
            .array()
            .of(yup.string().required())
            .min(1, () => t('form.spoken_languages_required'))
            .required(t('form.spoken_languages_required')),
          roles: yup
            .array()
            .of(
              yup.object({
                name: yup.string().required(),
                label: yup.string().required(),
                description: yup.string(),
                permissions: yup
                  .array()
                  .of(
                    yup.object({
                      name: yup.string().required(),
                      label: yup.string().required(),
                      description: yup.string(),
                    }),
                  )
                  .required(),
              }),
            )
            .min(1, t('form.role_number'))
            .required(t('form.role_required')),
          permissionNames: yup.array(yup.string().required()).required(),
        })
        .required(),
    [t],
  );
}
export type IUserForm = yup.InferType<ReturnType<typeof useUserSchema>>;

type Props = {
  userId?: number;
  defaultValues: IUserForm;
};

function UserForm({ userId, defaultValues }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const allRoles = useAllRolesQuery();
  const schema = useUserSchema();
  const toast = useToast();

  const userQuery = useOPUserQuery(userId?.toString() as unknown as number);
  const user = userQuery?.data;
  const userLanguage = user?.language;

  const userContext = useUserContext();
  const userUpdateMutation = useUpdateUserMutation();
  const userCreateMutation = useCreateUserMutation();
  const settingsQuery = useSettingsQuery();
  const spokenLanguagesByUser = user?.spoken_languages;
  const spokenLanguagesByUserUpperCase = spokenLanguagesByUser?.map((language) => language.toUpperCase()) ?? [];
  const allAvailableLanguages = userContext.config?.languages;

  const allAvailableLanguagesWithDuplicates = Object.values(allAvailableLanguages).reduce(
    (allLanguages, language) => allLanguages.concat(language),
    [],
  );
  const allAvailableLanguagesWithoutDuplicates = new Set(allAvailableLanguagesWithDuplicates);
  const allAvailableLanguagesWithoutDuplicatesArray = Array.from(allAvailableLanguagesWithoutDuplicates);
  const availableLanguagesToSelect = allAvailableLanguagesWithoutDuplicatesArray.filter((item) => !spokenLanguagesByUserUpperCase?.includes(item));
  const [availableLanguages, setAvailableLanguages] = useState(availableLanguagesToSelect);
  const [spokenLanguages, setSpokenLanguages] = useState(spokenLanguagesByUserUpperCase);

  const userTimezone = settingsQuery.data?.timezone;
  const timezones = [
    { label: 'Iceland GMT+0', value: 'Atlantic/Reykjavik' },
    { label: 'Europe/London GMT+0', value: 'Europe/London' },
    { label: 'Europe/Vienna GMT+1', value: 'Europe/Vienna' },
    { label: 'Europe/Athens GMT+2', value: 'Europe/Athens' },
  ];

  let allRolesOptions: Role[] = allRoles.data ?? [];

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    watch,
    setValue,
  } = form;

  const selectedTimezone = watch('timezone' as any);
  const selectedLanguage = watch('language' as any);

  useEffect(() => {
    setValue('language' as any, `${userLanguage}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLanguage]);

  useEffect(() => {
    setValue('timezone', `${selectedTimezone}`);
    setValue('language', `${selectedLanguage}`);
    setValue('spokenLanguages', spokenLanguages ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimezone, selectedLanguage, spokenLanguages]);

  useEffect(() => {
    setValue('timezone' as any, `${userTimezone}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTimezone]);

  useEffect(() => {
    if (settingsQuery?.data) {
      setAvailableLanguages(availableLanguagesToSelect);
      setSpokenLanguages(spokenLanguagesByUserUpperCase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spokenLanguagesByUser]);

  const onSubmit: SubmitHandler<IUserForm> = ({ roles, spokenLanguages, permissionNames, ...other }) => {
    const userData = {
      ...other,
      roles: roles.map((role) => {
        return role.name;
      }),
      spoken_languages: spokenLanguages,
      permissions: permissionNames ?? [],
    };
    userId
      ? userUpdateMutation.mutate(
          {
            userData,
            userId,
          },
          {
            onSuccess: () => {
              navigate(`/app/op/user/${userId}`);
            },
            onError: (error) => {
              toast({
                title: (error as Error & { response: { data?: { message?: string } } }).response?.data?.message,
                status: 'error',
                position: 'top',
              });
            },
          },
        )
      : userCreateMutation.mutate(userData, {
          onSuccess: () => {
            navigate('/app/op/users');
          },
          onError: (error) => {
            toast({
              title: (error as Error & { response: { data?: { message?: string } } }).response?.data?.message,
              status: 'error',
              position: 'top',
            });
          },
        });
  };

  function addSpokenLanguage(index: number) {
    //get selected language and remove it from availableLanguages
    const availableLanguagesCopy = [...availableLanguages];
    availableLanguagesCopy.splice(index, 1);
    setAvailableLanguages(availableLanguagesCopy);

    //add selected language to spokenLanguages
    const updatedSpokenLanguages = [...(spokenLanguages ?? [])];
    const selectedLanguage = availableLanguages.splice(index, 1);
    updatedSpokenLanguages.push(selectedLanguage[0]);
    setSpokenLanguages(updatedSpokenLanguages);
  }

  function removeSpokenLanguage(index: number) {
    //deletes selected spoken language from spokenLanguages array
    //removes deleted spoken language from spokenLanguages array
    const spokenLanguagesCopy = [...(spokenLanguages ?? [])];
    const selectedSpokenLanguage = spokenLanguagesCopy.splice(index, 1);
    setSpokenLanguages(spokenLanguagesCopy);

    //adds deleted spoken language to availableLanguages array
    const updatedAvailableLanguages = [...availableLanguages];
    updatedAvailableLanguages.push(selectedSpokenLanguage[0]);
    setAvailableLanguages(updatedAvailableLanguages);
  }

  function changeOrderUp(index: number) {
    //onClick gives language index - 1
    //get selected language and remove it from spokenLanguages
    const spokenLangCopy = [...(spokenLanguages ?? [])];
    spokenLangCopy.splice(index, 1);
    const selectedSpokenLanguageWithNewIndex = spokenLanguages?.splice(index, 1)[0];

    //inserts selected language in the spokenLanguages array at the right place
    spokenLangCopy.splice(index - 1, 0, selectedSpokenLanguageWithNewIndex as string);
    setSpokenLanguages(spokenLangCopy);
  }

  function changeOrderDown(index: number) {
    //onClick gives language index + 1
    //get selected language and remove it from spokenLanguages
    const spokenLangCopy = [...(spokenLanguages ?? [])];
    spokenLangCopy.splice(index, 1);
    const selectedSpokenLanguageWithNewIndex = spokenLanguages?.splice(index, 1)[0];

    //inserts selected language in the spokenLanguages array at the right place
    spokenLangCopy.splice(index + 1, 0, selectedSpokenLanguageWithNewIndex as string);
    setSpokenLanguages(spokenLangCopy);
  }

  if (userQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <FormProvider {...form}>
      <Stack gap={8} maxW="1000px" p="1" as="form" onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={2}>
            {simpleInput({
              name: 'name',
              label: t('user:name'),
              defaultValue: user?.name || '',
              register,
              errors,
              schema,
            })}
            {simpleInput({
              name: 'email',
              label: t('user:email'),
              defaultValue: user?.email || '',
              register,
              customHook: (val) => {
                setValue('email', val.replace(/ /g, ''));
              },
              errors,
              schema,
            })}
          </Stack>
          <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={2} alignItems={'flex-end'}>
            {dropdown({
              name: 'language',
              label: t('user:personal_settings.language.label'),
              showAsTooltip: true,
              description: t('user:personal_settings.language.explanation'),
              // @ts-ignore
              control,
              errors,
              options: createDropdownTranslatedStaticOptions({
                records: allAvailableLanguagesWithoutDuplicatesArray,
                translationKey: 'location:languages',
                initialOptions: { value: '', label: i18n.t('common:choose') },
              }),
            })}

            {dropdown({
              name: 'timezone',
              label: t('user:personal_settings.timezone'),
              defaultValue: userTimezone ? userTimezone : '',
              control,
              errors,
              options: createDropdownPlainOptions({
                records: timezones,
                valueColumn: 'value',
                labelColumn: 'label',
                initialOptions: userTimezone as any,
              }),
            })}
          </Stack>
        </Box>

        <Stack>
          <Heading size="xxs">{t('user:personal_settings.spoken_languages.label')}</Heading>
          <SimpleGrid alignItems={'baseline'} columns={{ lg: 2, base: 1 }} spacing={14}>
            <Box>
              <FormLabel flex={1}>{t('common:available_languages')}</FormLabel>
              <UnorderedList spacing={3} ml={0} mt={0} pt={0}>
                {availableLanguages.map((language, index) => {
                  return (
                    <HStack key={index} spacing={3}>
                      <Flex
                        width={'100%'}
                        pr={6}
                        pl={6}
                        height={'48px'}
                        fontSize={'md'}
                        alignItems={'center'}
                        border={'1px'}
                        borderColor={'gray.200'}
                        borderRadius={'8'}
                        bgColor={'white'}
                      >
                        <Text>{t(`location:languages.${language}`)}</Text>
                      </Flex>
                      <IconButton
                        icon={<AddIcon boxSize={4} />}
                        aria-label={t('common:add')}
                        p={'5'}
                        borderRadius={'6'}
                        fontSize={'xs'}
                        variant={'none'}
                        border={'1px'}
                        borderColor={'green'}
                        color={'green'}
                        size="lg"
                        _hover={{
                          bgColor: 'green.200',
                        }}
                        onClick={() => {
                          addSpokenLanguage(index);
                        }}
                      />
                    </HStack>
                  );
                })}
              </UnorderedList>
            </Box>

            <Box>
              <FormLabel>{t('user:personal_settings.spoken_languages.label')}</FormLabel>
              <OrderedList spacing={3} ml={0}>
                {spokenLanguages?.map((language: string, index: number) => {
                  return (
                    <HStack key={index} spacing={3}>
                      <Flex
                        border={'1px'}
                        borderColor={'gray.200'}
                        borderRadius={'8'}
                        bgColor={'white'}
                        width={'100%'}
                        height={'48px'}
                        pl={10}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        display={'flex'}
                      >
                        <ListItem fontSize={'md'}>{t(`location:languages.${language}`)}</ListItem>
                        <VStack>
                          <Button
                            mb={-3}
                            size="xs"
                            variant="none"
                            _hover={{
                              bgColor: 'gray.100',
                            }}
                            onClick={() => {
                              changeOrderUp(index);
                            }}
                            disabled={index === 0}
                          >
                            <motion.div whileHover={{ scale: 1.5 }} transition={{ duration: 0.2 }}>
                              <Icon as={FiChevronUp} />
                            </motion.div>
                          </Button>

                          <Button
                            size="xs"
                            variant="none"
                            _hover={{
                              bgColor: 'gray.100',
                            }}
                            onClick={() => {
                              changeOrderDown(index);
                            }}
                            disabled={index === spokenLanguages.length - 1}
                          >
                            <motion.div whileHover={{ scale: 1.5 }} transition={{ duration: 0.2 }}>
                              <Icon as={FiChevronDown} />
                            </motion.div>
                          </Button>
                        </VStack>
                      </Flex>
                      <IconButton
                        icon={<DeleteIcon boxSize={4} />}
                        aria-label={t('common:delete')}
                        padding={'5'}
                        borderRadius={'6'}
                        fontSize={'xs'}
                        variant={'none'}
                        border={'1px'}
                        borderColor={'red'}
                        color={'red'}
                        size="lg"
                        _hover={{
                          bgColor: 'red.200',
                        }}
                        onClick={() => {
                          removeSpokenLanguage(index);
                        }}
                      />
                    </HStack>
                  );
                })}
              </OrderedList>
            </Box>
          </SimpleGrid>
          <Text mt="2" color="error" fontSize="sm">
            {errors.spokenLanguages?.message}
          </Text>
        </Stack>

        <UserRolesAndPermissions roleOptions={allRolesOptions} mode={userId ? 'update' : 'create'} />

        <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
          <HStack>
            <Button
              color={'red.600'}
              borderColor={'red.600'}
              //@ts-ignores
              _hover={'pink.600'}
              variant="outline"
              onClick={() => {
                navigate(userId ? `/app/op/user/${userId}` : '/app/op/users');
              }}
            >
              {t('common:abort')}
            </Button>

            <Button type="submit" variant="primary">
              {t(userId ? 'common:update' : 'common:invite')}
            </Button>
          </HStack>
        </Flex>
      </Stack>
    </FormProvider>
  );
}
export default UserForm;
